/* MD5, because S3 wants Content-MD5 and the browser will not provide it.
 *
 * SubtleCrypto implements SHA-1 and above and deliberately omits MD5, on the
 * grounds that it is broken for anything security-critical. That is correct and
 * does not apply here: this is not a signature and nothing is trusted on the
 * strength of it. It is a checksum bound into the presigned PUT, which storage
 * compares against the bytes it actually received, so a truncated or corrupted
 * upload is rejected at the door rather than stored and discovered later by
 * someone trying to play it. TLS already prevents tampering; this catches the
 * accident, which is the failure that actually happens on a boat with one bar
 * of signal.
 *
 * The node computes the same value with hashlib and sends it the same way, so
 * both producers of objects in this system are checked identically.
 */

// Per-round left-rotation amounts.
const S = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
  5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
  4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
  6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
]

// K[i] = floor(2^32 * abs(sin(i+1))). Computed rather than pasted as a table:
// 64 magic constants transcribed by hand is 64 chances to be wrong in a way
// that only shows up as a rejected upload.
const K = (() => {
  const k = new Uint32Array(64)
  for (let i = 0; i < 64; i++) k[i] = Math.floor(Math.abs(Math.sin(i + 1)) * 4294967296)
  return k
})()

/** One 64-byte block, mixed into the running state.
 *
 *  Split out of the digest so the same core serves both a whole buffer and a
 *  stream of chunks. `dv` is read at `off`; nothing is copied. */
function block(dv: DataView, off: number, h: Uint32Array) {
  let A = h[0]!, B = h[1]!, C = h[2]!, D = h[3]!
  for (let i = 0; i < 64; i++) {
    let F: number
    let g: number
    if (i < 16) { F = (B & C) | (~B & D); g = i }
    else if (i < 32) { F = (D & B) | (~D & C); g = (5 * i + 1) % 16 }
    else if (i < 48) { F = B ^ C ^ D; g = (3 * i + 5) % 16 }
    else { F = C ^ (B | ~D); g = (7 * i) % 16 }

    F = (F + A + K[i]! + dv.getUint32(off + g * 4, true)) >>> 0
    A = D
    D = C
    C = B
    const s = S[i]!
    B = (B + (((F << s) | (F >>> (32 - s))) >>> 0)) >>> 0
  }
  h[0] = (h[0]! + A) >>> 0
  h[1] = (h[1]! + B) >>> 0
  h[2] = (h[2]! + C) >>> 0
  h[3] = (h[3]! + D) >>> 0
}

/** MD5 over a stream of chunks.
 *
 *  The reason this exists rather than one call over a whole file: a HydroMoth
 *  left recording continuously writes files of over a gigabyte, and hashing
 *  one by reading it whole needs the file in memory *twice* — once as the
 *  ArrayBuffer and again as the padded copy the digest walks. Two and a half
 *  gigabytes for one checksum is not a slow path, it is a failed allocation,
 *  and it surfaced as "could not be read" against a file that was perfectly
 *  readable.
 *
 *  Fed a slice at a time, the memory here is one chunk plus 64 bytes of
 *  carry-over, whatever the file's size.
 */
export class Md5 {
  private h = new Uint32Array([0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476])
  /** Bytes not yet part of a full 64-byte block. A chunk boundary almost never
   *  lands on one, so this is the normal case rather than an edge. */
  private tail = new Uint8Array(64)
  private tailLen = 0
  private tailView = new DataView(new ArrayBuffer(0))
  private total = 0

  constructor() {
    this.tailView = new DataView(this.tail.buffer)
  }

  update(bytes: Uint8Array): this {
    this.total += bytes.length
    let i = 0

    // Top up a partial block from the previous chunk first.
    if (this.tailLen > 0) {
      const want = Math.min(64 - this.tailLen, bytes.length)
      this.tail.set(bytes.subarray(0, want), this.tailLen)
      this.tailLen += want
      i = want
      if (this.tailLen < 64) return this
      block(this.tailView, 0, this.h)
      this.tailLen = 0
    }

    // Whole blocks straight out of the caller's buffer. The DataView is over
    // the same memory, so a chunk is walked without being copied.
    const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    for (; i + 64 <= bytes.length; i += 64) block(dv, i, this.h)

    if (i < bytes.length) {
      this.tail.set(bytes.subarray(i), 0)
      this.tailLen = bytes.length - i
    }
    return this
  }

  private out: Uint8Array | null = null

  /** Finish, and return the digest base64-encoded — the form S3 expects in
   *  Content-MD5. The instance must not be updated afterwards. */
  digestBase64(): string {
    const out = this.digest()
    let s = ''
    for (let i = 0; i < out.length; i++) s += String.fromCharCode(out[i]!)
    return btoa(s)
  }

  /** The same digest as lowercase hex.
   *
   *  This is an object's ETag. For a part of a multipart upload that is not
   *  server-side encrypted, S3's ETag *is* the MD5 of the part — so computing
   *  it here means the completion can name every part without reading a single
   *  response header. That matters: ETag is not a CORS-safelisted response
   *  header, so a browser cannot see it unless the bucket is configured to
   *  expose it, and an upload that depends on bucket CORS to finish is an
   *  upload that breaks on a config nobody remembers setting. */
  digestHex(): string {
    const out = this.digest()
    let s = ''
    for (let i = 0; i < out.length; i++) s += out[i]!.toString(16).padStart(2, '0')
    return s
  }

  private digest(): Uint8Array {
    if (this.out) return this.out
    // A 1 bit, zeros, then the length as a 64-bit little-endian count of bits.
    // That needs 9 free bytes, so it is one final block or two.
    const need = this.tailLen + 9
    const pad = new Uint8Array(need <= 64 ? 64 : 128)
    pad.set(this.tail.subarray(0, this.tailLen))
    pad[this.tailLen] = 0x80
    const pv = new DataView(pad.buffer)
    const bitLen = this.total * 8
    // Split across two 32-bit writes: writing only the low word would silently
    // produce a wrong digest for anything past 512 MB, and a checksum that is
    // wrong only for large inputs is the worst kind to debug. These files are
    // past that, so this is the live case, not a theoretical one.
    pv.setUint32(pad.length - 8, bitLen >>> 0, true)
    pv.setUint32(pad.length - 4, Math.floor(bitLen / 4294967296), true)
    for (let off = 0; off < pad.length; off += 64) block(pv, off, this.h)

    const out = new Uint8Array(16)
    const odv = new DataView(out.buffer)
    for (let i = 0; i < 4; i++) odv.setUint32(i * 4, this.h[i]!, true)
    this.out = out
    return out
  }
}

/** MD5 of `bytes`, base64-encoded — the form S3 expects in Content-MD5. */
export function md5Base64(bytes: Uint8Array): string {
  return new Md5().update(bytes).digestBase64()
}
